export interface AcordFieldItem {
  FieldName: string;
  Value: string | null;
  OtherIndex?: number;
  Append?: boolean;
}

function getField(data: AcordFieldItem[], fieldName: string): string {
  const matches = data.filter(d => d.FieldName === fieldName);
  if (matches.length === 0) return '';
  // Prefer the last non-empty value (API returns multiple policies; actual data often comes last)
  for (let i = matches.length - 1; i >= 0; i--) {
    const val = matches[i].Value ?? '';
    if (val) return val;
  }
  return '';
}

function resolve(data: AcordFieldItem[], baseName: string): string {
  const pv = getField(data, `F[0].P1[0].${baseName}[0]`);
  if (pv) return pv;
  return getField(data, baseName);
}

function parseDate(v: string): Date | null {
  if (!v) return null;
  const parts = v.split('/');
  if (parts.length !== 3) return null;
  return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
}

function isTruthy(v: string): boolean {
  if (!v) return false;
  return ['x', '1', 'y', 'yes', 'true'].includes(v.trim().toLowerCase());
}

export type FieldType = 'str' | 'bool' | 'date';

export interface FieldDef {
  apiName: string;
  type: FieldType;
}

export const str = (apiName: string): FieldDef => ({ apiName, type: 'str' });
export const bool = (apiName: string): FieldDef => ({ apiName, type: 'bool' });
export const date = (apiName: string): FieldDef => ({ apiName, type: 'date' });

export type FieldMap = Record<string, FieldDef>;

export function mapFields(data: AcordFieldItem[], fieldMap: FieldMap): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, def] of Object.entries(fieldMap)) {
    const raw = resolve(data, def.apiName);
    if (def.type === 'bool') result[key] = isTruthy(raw);
    else if (def.type === 'date') result[key] = parseDate(raw);
    else result[key] = raw;
  }
  return result;
}

function getFieldAtIndex(data: AcordFieldItem[], fieldName: string, index: number): string {
  const matches = data.filter(d => d.FieldName === fieldName && d.OtherIndex === index);
  if (matches.length === 0) return '';
  // Prefer the last non-empty value
  for (let i = matches.length - 1; i >= 0; i--) {
    const val = matches[i].Value ?? '';
    if (val) return val;
  }
  return '';
}

function resolveAtIndex(data: AcordFieldItem[], baseName: string, index: number): string {
  const pv = getFieldAtIndex(data, `F[0].P1[0].${baseName}[0]`, index);
  if (pv) return pv;
  const baseNameB = baseName.replace(/_A$/, '_B');
  if (baseNameB !== baseName) {
    const pvB = getFieldAtIndex(data, `F[0].P1[0].${baseNameB}[0]`, index);
    if (pvB) return pvB;
  }
  return getFieldAtIndex(data, baseName, index);
}

export function mapFieldsAtIndex(data: AcordFieldItem[], fieldMap: FieldMap, index: number): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, def] of Object.entries(fieldMap)) {
    const raw = resolveAtIndex(data, def.apiName, index);
    if (def.type === 'bool') result[key] = isTruthy(raw);
    else if (def.type === 'date') result[key] = parseDate(raw);
    else result[key] = raw;
  }
  return result;
}


export function getAppendedField(data: AcordFieldItem[], baseName: string): string {
  const prefixed = `F[0].P1[0].${baseName}[0]`;
  const matches = data.filter(d => (d.FieldName === prefixed || d.FieldName === baseName) && d.Append);
  const raw = matches.map(d => d.Value ?? '').join('');
  const seen = new Set<string>();
  const uniqueLines = raw.split('\n').filter(line => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (seen.has(trimmed)) return false;
    seen.add(trimmed);
    return true;
  });
  return uniqueLines.join('\n');
}

// Includes all remark entries regardless of Append flag (used for prefill where we want every line)
export function getAllRemarkLines(data: AcordFieldItem[], baseName: string): string {
  const prefixed = `F[0].P1[0].${baseName}[0]`;
  const matches = data.filter(d => (d.FieldName === prefixed || d.FieldName === baseName) && d.Value);
  const raw = matches.map(d => d.Value ?? '').join('');
  const seen = new Set<string>();
  const uniqueLines = raw.split('\n').filter(line => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (seen.has(trimmed)) return false;
    seen.add(trimmed);
    return true;
  });
  return uniqueLines.join('\n');
}

export function resolveFieldAtIndex(data: AcordFieldItem[], baseName: string, index: number): string {
  return resolveAtIndex(data, baseName, index);
}

export interface PolicyAddressEntry {
  PolicyId: string;
  Number: string;
  AddressLine1: string;
  AddressLine2: string;
  City: string;
  State: string;
  ZipCode: string;
  County: string | null;
}

// Parses the "PolicyAddresses" row (a JSON-stringified array) out of a get-coverages response.
export function getPolicyAddresses(data: AcordFieldItem[]): PolicyAddressEntry[] {
  const raw = getField(data, 'PolicyAddresses');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Picks the address to use for the INSURED block. When a master certificate has more than one
// policy, only the first selected policy's address is considered — other policies' addresses are
// ignored, even if the first policy has none (callers should fall back to the client address then).
// Returns null if no usable (non-empty AddressLine1) address is present for that policy.
export function pickPolicyAddress(entries: PolicyAddressEntry[], firstSelectedPolicyId?: string): PolicyAddressEntry | null {
  if (entries.length === 0 || !firstSelectedPolicyId) return null;
  const match = entries.find(e => e.PolicyId === firstSelectedPolicyId);
  return match && match.AddressLine1 ? match : null;
}

export interface InsurerSlot { name: string; naic: string; }

export function getInsurers(data: AcordFieldItem[]): Record<string, InsurerSlot> {
  const slots: Record<string, InsurerSlot> = {};
  for (const letter of ['A', 'B', 'C', 'D', 'E', 'F']) {
    slots[letter] = {
      name: resolve(data, `Insurer_FullName_${letter}`),
      naic: resolve(data, `Insurer_NAICCode_${letter}`),
    };
  }
  return slots;
}
